// src/shared/game/npc/registry.ts — Plan 5

import type { GameState } from '../types';
import type { Npc, NpcKind } from './types';
import { appendEvent } from '../event-log';

/** Returns the NPC with the given id, or undefined. O(n) scan. */
export function findNpcById(state: GameState, npcId: string): Npc | undefined {
  return state.npcs.find((npc) => npc.id === npcId);
}

/**
 * Despawns an NPC by id. Removes it from state.npcs and emits an NpcDespawnedEvent.
 * No-op if the NPC is not found.
 *
 * @internal Reserved for Plan 6 game-over cleanup flow. No current consumers in Plan 5.
 * TODO(plan-6): wire into game-over path to remove all NPCs when the game ends.
 */
export function despawnNpc(
  state: GameState,
  npcId: string,
  reason: 'replaced_by_new_stalker' | 'game_over' | 'killed_player',
): void {
  const idx = state.npcs.findIndex((npc) => npc.id === npcId);
  if (idx === -1) return; // no-op

  state.npcs = state.npcs.filter((npc) => npc.id !== npcId);
  appendEvent(state, {
    type: 'npc_despawned',
    playerId: null,
    npcId,
    reason,
  });
}

/**
 * Removes ALL NPCs of the given kind. Returns the number removed.
 * Used by spawnShadowStalker to enforce the single-stalker invariant (spec §4 rule 4).
 * Each removed NPC emits an NpcDespawnedEvent.
 */
export function removeAllNpcsOfKind(
  state: GameState,
  kind: NpcKind,
  reason: 'replaced_by_new_stalker' | 'game_over',
): number {
  const toRemove = state.npcs.filter((npc) => npc.kind === kind);
  if (toRemove.length === 0) return 0;

  state.npcs = state.npcs.filter((npc) => npc.kind !== kind);

  for (const npc of toRemove) {
    appendEvent(state, {
      type: 'npc_despawned',
      playerId: null,
      npcId: npc.id,
      reason,
    });
  }

  return toRemove.length;
}

/**
 * Returns true if the NPC is invulnerable to all damage. Spec §4 rule 5: Shadow Stalker
 * cannot be damaged by any item. This is currently true for ALL NPC kinds (only
 * shadow_stalker exists). Future NPC kinds can override.
 *
 * NOTE: Plan 4a items target only state.players.get(...); state.npcs is never a target.
 * AoE items (Toxic Cloud, Meteor Strike) read player.nodeId, not NPC.nodeId.
 * Therefore no current item path needs this predicate. Plan 4b's Orbital Cannon
 * will be the first consumer.
 */
export function isNpcInvulnerable(npc: Npc): boolean {
  return npc.kind === 'shadow_stalker';
}
